"use client";

import {
	Button,
	type DefaultLinkProps,
	Image,
	Link,
	Price,
	type PriceProps,
	Rating,
	RichText,
	Tag,
	Text
} from "@pamperedchef/design-system";
import type { BlockOf } from "@/domain/catalog";
import { splitUpsellBody } from "../../helpers/upsell-set/split-upsell-body";
import * as styles from "./UpsellSet.module.scss";

/** The set itself, rendered as the product card beside the copy. */
export interface UpsellSetFeaturedProduct {
	sku: string;
	name: string;
	href: string;
	image: { src: string; alt?: string };
	price?: PriceProps;
	rating?: { rating: number; reviewCount?: number };
	isVirtualProduct?: boolean;
}

/** Catalog data behind an `upsell_set_block`, resolved by whoever renders it. */
export interface UpsellSetData {
	featured?: UpsellSetFeaturedProduct;
	/** The product whose PDP this is. Never authored into the list. */
	current?: { sku: string; name: string };
}

export interface UpsellSetProps {
	block: BlockOf<"upsell_set_block">;
	resolved?: UpsellSetData;
	addToCartLabel?: string;
	onAddToCart?: (sku: string) => void;
}

/**
 * Adapter for the `upsell_set_block` — Figma **"Upsell Banner"** (node
 * 6397:219946). Tinted banner pairing CMS-authored copy with a card for the set.
 */
export function UpsellSet({
	block,
	resolved,
	addToCartLabel = "Add to Cart",
	onAddToCart
}: UpsellSetProps) {
	const featured = resolved?.featured;
	const current = resolved?.current;
	const { intro, items } = splitUpsellBody(block.body);

	return (
		<section className={styles["upsell"]} data-block="upsell_set_block">
			<div className={styles["upsell__copy"]}>
				<h2 className={styles["upsell__headline"]}>
					{block.intro_headline}
				</h2>

				{intro ? (
					<div className={styles["upsell__body"]}>
						<RichText content={intro} />
					</div>
				) : null}

				{items.length > 0 || current ? (
					<ul className={styles["upsell__list"]}>
						{withCurrentSecond(items, current).map((item, i) =>
							typeof item === "string" ? (
								<li
									key={`item-${i}`}
									className={styles["upsell__item"]}
									dangerouslySetInnerHTML={{ __html: item }}
								/>
							) : (
								<li
									key={item.sku}
									className={styles["upsell__item"]}
								>
									<span
										className={styles["upsell__item-row"]}
									>
										<Text
											tag="span"
											className={
												styles["upsell__item-name"]
											}
										>
											{item.name}
										</Text>
										<Tag
											kind="subtle"
											size="xs"
											color="emerald"
											label="Set Exclusive"
										/>
									</span>
								</li>
							)
						)}
					</ul>
				) : null}
			</div>

			{featured ? (
				<article className={styles["upsell__card"]}>
					<a
						href={featured.href}
						aria-hidden="true"
						tabIndex={-1}
						className={styles["upsell__card-image-link"]}
					>
						<Image
							src={featured.image.src}
							alt={featured.image.alt ?? ""}
							aspectRatio="1:1"
							className={styles["upsell__card-image"]}
						/>
					</a>

					<div className={styles["upsell__card-content"]}>
						<Link<DefaultLinkProps>
							href={featured.href}
							size="lg"
							className={styles["upsell__card-name"]}
						>
							{featured.name}
						</Link>

						{featured.rating ? (
							<Rating
								{...featured.rating}
								size="sm"
								className={styles["upsell__card-rating"]}
							/>
						) : null}

						{featured.price ? (
							<Price
								{...featured.price}
								size="medium"
								theme="dark"
								className={styles["upsell__card-price"]}
							/>
						) : null}

						{!featured.isVirtualProduct ? (
							<Button
								kind="secondary"
								size="sm"
								className={styles["upsell__card-cta"]}
								onClick={() => onAddToCart?.(featured.sku)}
							>
								{addToCartLabel}
							</Button>
						) : null}
					</div>
				</article>
			) : null}
		</section>
	);
}

type ListItem = string | NonNullable<UpsellSetData["current"]>;

/** Figma places the current product second in the list. */
function withCurrentSecond(
	items: string[],
	current: UpsellSetData["current"]
): ListItem[] {
	if (!current) return items;
	return [...items.slice(0, 1), current, ...items.slice(1)];
}
